import { type FormEvent, useState } from "react";

const CATEGORIES = [
  ["sexual", "色情 / NSFW", "Sexual / NSFW"],
  ["graphic_violence", "暴力 / 血腥", "Graphic violence / gore"],
  ["hate", "仇恨言论", "Hate speech"],
  ["child_safety", "儿童安全 / CSAM", "Child safety / CSAM"],
  ["deepfake_impersonation", "深度伪造 / 冒充", "Deepfake / impersonation"],
  ["intellectual_property", "版权 / 商标侵权", "Copyright / trademark"],
  ["privacy", "隐私与数据权利", "Privacy and data rights"],
  ["billing", "账单 / 退款", "Billing / refund"],
  ["fraud", "欺诈 / 账户安全", "Fraud / account security"],
  ["other", "其他 / 申诉", "Other / appeal"],
] as const;

export function ComplianceReportForm({ language }: { language: "zh" | "en" }) {
  const zh = language === "zh";
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [reportId, setReportId] = useState("");

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const values = new FormData(form);
    setBusy(true);
    setError("");
    setReportId("");
    try {
      const response = await fetch("/api/compliance/reports", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          email: String(values.get("email") ?? ""),
          category: String(values.get("category") ?? "other"),
          contentUrl: String(values.get("contentUrl") ?? ""),
          description: String(values.get("description") ?? ""),
          consent: values.get("consent") === "on",
        }),
      });
      const body = (await response.json()) as { reportId?: string; error?: string };
      if (!response.ok) throw new Error(body.error ?? `Request failed (${response.status})`);
      setReportId(body.reportId ?? "");
      form.reset();
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : zh
            ? "提交失败，请稍后重试"
            : "Could not submit the report. Please try again.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="report-page">
      <span className={zh ? "eyebrow" : "kicker"}>
        {zh ? "联系与举报 / SAFETY" : "CONTACT & REPORT / SAFETY"}
      </span>
      <h1>{zh ? "让问题有编号，也有后续。" : "Give the issue a reference — and a follow-up."}</h1>
      <p className="report-intro">
        {zh
          ? "用于内容安全、儿童安全、冒充、版权、隐私、账户安全、账单退款和误判申诉。请勿在说明中粘贴违法图片或重复传播有害内容。"
          : "Use this form for content safety, child safety, impersonation, IP, privacy, account security, billing/refunds, or an appeal. Do not paste illegal images or redistribute harmful material."}
      </p>
      <form className="report-form" onSubmit={submit}>
        <label>
          {zh ? "联系邮箱" : "Contact email"}
          <input type="email" name="email" required maxLength={254} />
        </label>
        <label>
          {zh ? "问题类别" : "Category"}
          <select name="category" required defaultValue="other">
            {CATEGORIES.map(([value, labelZh, labelEn]) => (
              <option value={value} key={value}>
                {zh ? labelZh : labelEn}
              </option>
            ))}
          </select>
        </label>
        <label>
          {zh ? "相关页面或内容 URL（可选）" : "Related page or content URL (optional)"}
          <input type="url" name="contentUrl" maxLength={500} placeholder="https://" />
        </label>
        <label>
          {zh ? "问题说明" : "Description"}
          <textarea
            name="description"
            required
            minLength={20}
            maxLength={4000}
            rows={8}
            placeholder={
              zh
                ? "请说明发生了什么、相关订单/举报编号，以及希望我们采取的措施……"
                : "Explain what happened, any order/report reference, and the action you request…"
            }
          />
        </label>
        <label className="report-consent">
          <input type="checkbox" name="consent" required />
          <span>
            {zh
              ? "我同意结一为处理本请求而保存和使用上述信息。"
              : "I consent to Jieyi storing and using this information to handle the request."}
          </span>
        </label>
        {error && <p className="form-error">{error}</p>}
        {reportId && (
          <p className="report-success" role="status">
            {zh ? "已收到。举报编号：" : "Received. Report reference: "}
            <strong>{reportId}</strong>
          </p>
        )}
        <button type="submit" className="primary-button" disabled={busy}>
          {busy ? (zh ? "提交中…" : "Submitting…") : zh ? "提交请求" : "Submit report"}
        </button>
      </form>
      <p className="report-response-note">
        {zh
          ? "儿童安全、恐怖主义等严重事项优先；其他请求通常在 3 个工作日内初步响应。"
          : "Child-safety and terrorism matters are prioritised; other requests normally receive an initial response within three business days."}
      </p>
    </section>
  );
}
